import { FC, useEffect, useRef, useState } from "react";
import "./Navbar.scss";
import { Col, Container, Row } from "react-bootstrap";
import { useAuth } from "../../providers/AuthProvider";
import { toast } from "react-toastify";
import React from "react";
import Breadcrumb from "../Breadcrumb/Breadcrumb";
import { Link } from "react-router-dom";
import { BvIcon } from "bevi-icon";
import { UserRoleEnum } from "../../enums/User/UserRoleEnum";
import { useUsers } from "../../providers/Access/Users/UsersProvider";
import { ReactComponent as VectorDow } from '../../assets/icons/Vector 2.svg';
import { ReactComponent as Logout } from '../../assets/icons/logout-solid-600.svg';
import logoWhite from '../../assets/img/bevi-ajuda-white.svg';
import { useMediaQuery } from "react-responsive";

interface NavbarProps {
  title: string;
  subtitle: string;
  icon: string;
}

const getInitials = (name?: string) => {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  if(parts.length === 1)return parts[0].charAt(0).toLocaleUpperCase();
  return (parts[0].charAt(0) + parts[parts.length -1].charAt(0)).toLocaleUpperCase();
}

const getFirstLastName = (name?: string) => {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1]}`;
};

const Navbar: FC<NavbarProps> = () => {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuClosing, setIsMobileMenuClosing] = useState(false);
  const [isSidenavOpen, setIsSidenavOpen] = useState(false);
  const { userLogged, logout, profileImageUrl } = useAuth();
  const { statusTutorial } = useUsers();
  const { handleStatusTutorial } = useUsers();
  const userMenuRef = useRef<HTMLDivElement>(null);
  const isPhone = useMediaQuery({ maxWidth: 576 });

  useEffect(() => {
    const updateSidenavState = () => {
      const sidenav = document.getElementsByClassName("g-sidenav-pinned")[0];
      if (sidenav) {
        setIsSidenavOpen(!sidenav.classList.contains("g-sidenav-hidden"));
      }
    };

    updateSidenavState();

    const observer = new MutationObserver(updateSidenavState);
    const target = document.getElementsByClassName("g-sidenav-pinned")[0];
    if (target) {
      observer.observe(target, { attributes: true, attributeFilter: ["class"] });
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    handleStatusTutorial();
  }, []);

  useEffect(() => {
    if (isPhone) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isUserMenuOpen, isPhone]);

  const closeMobileMenu = () => {
    setIsMobileMenuClosing(true);
    setTimeout(() => {
      setIsUserMenuOpen(false);
      setIsMobileMenuClosing(false);
    }, 280);
  };

  const toggleSidebar = () => {
    if (document.querySelector(".sidenav-toggler")) {
      let sidenavShow = document.getElementsByClassName("g-sidenav-pinned")[0];
      let toggleNavbarMinimize = document.getElementById("navbarMinimize");

      if (sidenavShow) {
        if (!sidenavShow.classList.contains("g-sidenav-hidden")) {
          sidenavShow.classList.remove("g-sidenav-show");
          sidenavShow.classList.add("g-sidenav-hidden");
          if (toggleNavbarMinimize) {
            toggleNavbarMinimize.click();
            toggleNavbarMinimize.setAttribute("checked", "true");
          }
        } else {
          sidenavShow.classList.remove("g-sidenav-hidden");
          sidenavShow.classList.add("g-sidenav-show");

          if (toggleNavbarMinimize) {
            toggleNavbarMinimize.click();
            toggleNavbarMinimize.removeAttribute("checked");
          }
        }
      }
    }
  };

  const handleLogout = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const result = await logout().then();
    if (result?.success) {
      window.location.href = "/login-notice";
    } else {
      toast.error(`Erro ao sair: ${result?.error}`);
    }
  };

  return (
    <>
      <nav
        className="navbar navbar-main navbar-expand-lg px-0 mx-4 shadow-none border-radius-xl z-index-sticky Navbar"
        id="navbarBlur"
        data-scroll="false"
        data-testid="Navbar"
      >
        <Container fluid className="d-block py-1 px-0">
          <Row className="w-100">
            <Col md={12}>
              <div className="d-flex justify-content-between align-items-center w-100">
                <div className="d-md-flex d-none align-items-center">
                  <div className="sidenav-toggler sidenav-toggler-inner me-3 p-2">
                    <button
                      type="button"
                      className="nav-link p-0"
                      onClick={() => toggleSidebar()}
                    >
                      <div className="sidenav-toggler-inner">
                        <i className="sidenav-toggler-line bg-white"></i>
                        <i className="sidenav-toggler-line bg-white"></i>
                        <i className="sidenav-toggler-line bg-white"></i>
                      </div>
                    </button>
                  </div>
                  {!isSidenavOpen && (
                    <Link to="/" className="navbar-logo-link">
                      <img src={logoWhite} alt="BeviAjuda" className="navbar-logo-white" />
                    </Link>
                  )}
                </div>
                <div className="w-100">
                  <div
                    className="navbar-collapse d-flex align-items-center gap-4 mt-sm-0 mt-2 me-md-0 me-sm-4 justify-content-between"
                    id="navbar"
                  >
                    <ul className="navbar-nav justify-content-end flex-row gap-1 align-items-center w-auto ms-md-auto">
                      {/* Mobile: hambúrguer (esquerda) */}
                      <li className="nav-item d-md-none d-flex align-items-center">
                        <div className="sidenav-toggler sidenav-toggler-inner py-2 ps-2">
                          <button
                            type="button"
                            className="nav-link p-0"
                            onClick={() => toggleSidebar()}
                          >
                            <div className="sidenav-toggler-inner">
                              <i className="sidenav-toggler-line bg-white"></i>
                              <i className="sidenav-toggler-line bg-white"></i>
                              <i className="sidenav-toggler-line bg-white"></i>
                            </div>
                          </button>
                        </div>
                      </li>

                      {userLogged && !isPhone && (
                        <li className="nav-item d-none d-md-flex align-items-center">
                          <span className="navbar-user-summary">
                            {userLogged?.partner_code}
                            {userLogged?.partner_code && " - "}
                            {getFirstLastName(userLogged?.name)}
                          </span>
                        </li>
                      )}

                      {userLogged &&
                        (userLogged?.role == UserRoleEnum.ADMIN || userLogged?.access_profile?.id != 2) && (
                          <li className="nav-item d-flex align-items-center ms-auto ms-md-0">
                            {/* Tutorial obrigatório desativado — sempre vai direto pro chat. */}
                            {/* {statusTutorial === 0 ? (
                              <Link
                                className="nav-link text-white font-weight-bold px-0 cursor-pointer ia-beviajuda-link"
                                to={"/chats/tutorial"}
                              >
                                IA Beviajuda
                              </Link>
                            ) : ( */}
                              <Link
                                className="nav-link font-weight-bold px-0 cursor-pointer ia-beviajuda-link"
                                to={"/chats/reply"}
                              >
                                IA Beviajuda
                              </Link>
                            {/* )} */}
                          </li>
                        )}

                      {/* Mobile: avatar (direita, ao lado do IA Beviajuda) */}
                      <li className="nav-item d-md-none d-flex align-items-center mobile-avatar-item">
                        <button
                          type="button"
                          className="user-menu-trigger"
                          onClick={() => setIsUserMenuOpen((prev) => !prev)}
                        >
                          {profileImageUrl ? (
                            <img
                              src={profileImageUrl}
                              alt="Avatar"
                              className="user-menu-avatar"
                            />
                          ) : (
                            <div className="user-menu-avatar user-menu-avatar-placeholder d-flex align-items-center justify-content-center">
                              <span className="user-menu-initials user-menu-initials-sm">{getInitials(userLogged?.name)}</span>
                            </div>
                          )}
                        </button>
                      </li>

                      {/* Desktop: avatar menu */}
                      <li className="nav-item d-md-flex d-none align-items-center justify-content-end">
                        <div className="user-menu-wrapper" ref={userMenuRef}>
                          <button
                            type="button"
                            className="user-menu-trigger"
                            onClick={() => setIsUserMenuOpen((prev) => !prev)}
                          >
                            {profileImageUrl ? (
                              <img
                                src={profileImageUrl}
                                alt="Avatar"
                                className="user-menu-avatar"
                              />
                            ) : (
                              <div className="user-menu-avatar user-menu-avatar-placeholder d-flex align-items-center justify-content-center">
                                <span className="user-menu-initials user-menu-initials-sm">{getInitials(userLogged?.name)}</span>
                              </div>
                            )}
                            <VectorDow
                              className={`user-menu-chevron ${isUserMenuOpen ? "open" : ""}`}
                              width={6}
                              height={3}
                            />
                          </button>

                          {isUserMenuOpen && !isPhone && (
                            <div className="user-menu-dropdown">
                              <div className="user-menu-header">
                                {profileImageUrl ? (
                                  <img
                                    src={profileImageUrl}
                                    alt="Avatar"
                                    className="user-menu-avatar-lg"
                                  />
                                ) : (
                                  <div className="user-menu-avatar-lg user-menu-avatar-placeholder d-flex align-items-center justify-content-center">
                                    <span className="user-menu-initials user-menu-initials-lg">{getInitials(userLogged?.name)}</span>
                                  </div>
                                )}
                                <h6 className="user-menu-greeting">
                                  Olá, <span className="txt-username">{userLogged?.name?.split(" ")[0].toLowerCase() ?? ""}!</span>
                                </h6>
                                {userLogged?.partner_code && (
                                  <span className="user-menu-partner-code">
                                    {userLogged.partner_code}
                                  </span>
                                )}
                              </div>

                              <ul className="user-menu-list">
                                {userLogged?.role === UserRoleEnum.ADMIN && (
                                <li>
                                  <Link
                                    to="/profile/account"
                                    className="user-menu-item"
                                    onClick={() => setIsUserMenuOpen(false)}
                                  >
                                    <span className="user-menu-item-left">
                                      <BvIcon name="settings" variant="dark" height={16} width={16} />
                                      Gerenciar conta
                                    </span>
                                    <BvIcon name="chevronRight" height={16} width={16}/>
                                  </Link>
                                </li>
                                )}

                                {userLogged?.role === UserRoleEnum.ADMIN && (
                                  <li>
                                    <Link
                                      to="/home"
                                      className="user-menu-item"
                                      onClick={() => setIsUserMenuOpen(false)}
                                    >
                                      <span className="user-menu-item-left">
                                        <BvIcon name="dashboardScreen" variant="dark" height={16} width={16} />
                                        Painel Adm.
                                      </span>
                                      <BvIcon name="chevronRight" height={16} width={16}/>
                                    </Link>
                                  </li>
                                )}

                                <li>
                                  <a
                                    className="user-menu-item"
                                    onClick={(e) => { setIsUserMenuOpen(false); handleLogout(e); }}
                                  >
                                    <span className="user-menu-item-left">
                                      <Logout className="user-menu-item-icon" width={16} height={16} />
                                      Sair
                                    </span>
                                    <BvIcon name="chevronRight" height={16} width={16}/>
                                  </a>
                                </li>
                              </ul>
                            </div>
                          )}
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </nav>

      {isUserMenuOpen && isPhone && userLogged && (
        <div className={`user-menu-mobile-overlay ${isMobileMenuClosing ? "closing" : ""}`} onClick={closeMobileMenu}>
          <div className={`user-menu-mobile-sheet ${isMobileMenuClosing ? "closing" : ""}`} onClick={(e) => e.stopPropagation()}>
            <div className="user-menu-mobile-header">
              {profileImageUrl ? (
                <img
                  src={profileImageUrl}
                  alt="Avatar"
                  className="user-menu-avatar-lg"
                />
              ) : (
                <div className="user-menu-avatar-lg user-menu-avatar-placeholder d-flex align-items-center justify-content-center">
                  <span className="user-menu-initials user-menu-initials-lg">{getInitials(userLogged?.name)}</span>
                </div>
              )}
              <h6 className="user-menu-greeting">
                Olá, <span className="txt-username">{userLogged?.name?.split(" ")[0].toLowerCase() ?? ""}!</span>
              </h6>
              {userLogged?.partner_code && (
                <span className="user-menu-partner-code">
                  {userLogged.partner_code}
                </span>
              )}
            </div>

            <ul className="user-menu-list">
              {userLogged?.role === UserRoleEnum.ADMIN && (
              <li>
                <Link
                  to="/profile/account"
                  className="user-menu-item"
                  onClick={closeMobileMenu}
                >
                  <span className="user-menu-item-left">
                    <BvIcon name="settings" variant="dark" height={16} width={16} />
                    Gerenciar conta
                  </span>
                  <BvIcon name="chevronRight" height={16} width={16}/>
                </Link>
              </li>
              )}

              {userLogged?.role === UserRoleEnum.ADMIN && (
                <li>
                  <Link
                    to="/home"
                    className="user-menu-item"
                    onClick={closeMobileMenu}
                  >
                    <span className="user-menu-item-left">
                      <BvIcon name="dashboardScreen" variant="dark" height={16} width={16} />
                      Painel Adm.
                    </span>
                    <BvIcon name="chevronRight" height={16} width={16}/>
                  </Link>
                </li>
              )}

              <li>
                <a
                  className="user-menu-item"
                  onClick={(e) => { closeMobileMenu(); handleLogout(e); }}
                >
                  <span className="user-menu-item-left">
                    <Logout className="user-menu-item-icon" width={16} height={16} />
                    Sair
                  </span>
                  <BvIcon name="chevronRight" height={16} width={16}/>
                </a>
              </li>
            </ul>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
